// A step change in words (docs/step-changes-brief.md, 2026-10-24): one record for every change to the process, filed through
// ug.step_change_file. The screens and the connector read the same record and say the same things about it: the kind word,
// what Approve writes ("add Waiting on the client's answer as step 2 of Decision"), each proposed value in the words the
// tables use, and whether a suggestion is stale (its row no longer holds what it held when filed). Pure: the names are
// looked up through the context the caller passes.

export type StepChangeKind = 'add' | 'change' | 'retire' | 'stage_setting' | 'instructions';
export type StepChangeStatus = 'waiting' | 'applied' | 'approved' | 'sent_back' | 'replaced' | 'undone';
export type StepChangeTable = 'pipeline_step' | 'workflow_step' | 'pipeline_stage';

export interface StepChangeRow {
  id: string;
  target_table: StepChangeTable;
  target_id: string | null;
  stage_id: string | null;
  kind: StepChangeKind;
  proposed: Record<string, unknown>;
  before: Record<string, unknown>;
  applied?: Record<string, unknown> | null;
  undo?: Record<string, unknown> | null;
  reason?: string | null;
  status: StepChangeStatus;
  suggested_by?: string | null;
  suggested_at: string;
  decided_by?: string | null;
  decided_at?: string | null;
  undone_at?: string | null;
  note?: string | null;
}

/** What the words need to know: names by id, labels by key, open leads on a step. */
export interface StepChangeContext {
  stepName: (table: 'pipeline_step' | 'workflow_step', id: string | null | undefined) => string | null;
  stageName: (id: string | null | undefined) => string | null;
  seatName: (id: string | null | undefined) => string | null;
  conditionLabel: (key: string | null | undefined) => string | null;
  eventLabel: (key: string | null | undefined) => string | null;
  /** The open leads standing on a pipeline step now. */
  leadsOn: (stepId: string) => number;
  /** A job tab's name on the strip (the step that opens it); blank when unknown. */
  tabName?: (tab: string | null | undefined) => string | null;
}

export const KIND_WORD: Record<StepChangeKind, string> = { add: 'Add', change: 'Change', retire: 'Retire', stage_setting: 'Stage setting', instructions: 'Instructions' };

const DONE_RULES: Record<string, string> = { seat_ticks: 'The seat ticks it', owner_approves: 'Owner approves', event: 'Something happens in ZeroGap', client_acts: 'The client acts' };

/** The column's label as the tables show it. */
export function stepColumnLabel(table: StepChangeTable, col: string): string {
  const common: Record<string, string> = { name: 'Name', seat_id: 'Seat', note: 'Note', instructions: 'Instructions', position: 'Position', gate: 'Gate' };
  const pipeline: Record<string, string> = { applies_when: 'Applies when', done_rule: 'Done when', event_key: 'Its event', done_when: 'Description', review_link: 'Review link', back_to_step_id: 'Back to', tick_words: 'Tick words', second_answer: 'Second answer', sets_answer: 'Records', replaced_by_step_id: 'Replaced by' };
  const job: Record<string, string> = { only_when_attribute: 'Applies when', only_when_value: 'Applies when, value', lock_point: 'Lock point', target_offset_days: 'Day', tab: 'Opens', check_depth: 'Check', owner_role: 'Role' };
  const stage: Record<string, string> = { default_likelihood_pct: 'Likelihood', likelihood_by_lane: 'Likelihood by lane', quiet_days: 'Quiet days' };
  return common[col] ?? (table === 'pipeline_step' ? pipeline[col] : table === 'workflow_step' ? job[col] : stage[col]) ?? col.replace(/_/g, ' ');
}

/** One value in words: a seat's name, a condition's label, Yes / No, "blank". */
export function stepValueWords(table: StepChangeTable, col: string, value: unknown, ctx: StepChangeContext): string {
  if (value === null || value === undefined || value === '') return col === 'applies_when' && table === 'pipeline_step' ? 'Every lead' : col === 'back_to_step_id' || col === 'gate' || col === 'review_link' || col === 'lock_point' ? 'No' : 'blank';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (col === 'seat_id') return ctx.seatName(String(value)) ?? '?';
  if (col === 'back_to_step_id' || col === 'replaced_by_step_id') return ctx.stepName('pipeline_step', String(value)) ?? '?';
  if (col === 'applies_when') return ctx.conditionLabel(String(value)) ?? String(value);
  if (col === 'event_key') return ctx.eventLabel(String(value)) ?? String(value);
  if (col === 'done_rule') return DONE_RULES[String(value)] ?? String(value);
  if (col === 'tab') return ctx.tabName?.(String(value)) ?? String(value).replace(/_/g, ' ');
  if (col === 'likelihood_by_lane' && typeof value === 'object') return Object.entries(value as Record<string, unknown>).map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}%`).join(', ');
  if (col === 'default_likelihood_pct') return `${String(value)}%`;
  if (col === 'quiet_days') return `${String(value)} day${Number(value) === 1 ? '' : 's'}`;
  if (col === 'only_when_attribute' || col === 'only_when_value' || col === 'owner_role' || col === 'check_depth') return String(value).replace(/_/g, ' ');
  return String(value);
}

/** The done-when as one phrase: the rule, or the event's label. */
export function doneWhenWords(values: Record<string, unknown>, ctx: StepChangeContext): string {
  const rule = values.done_rule as string | undefined;
  if (!rule) return '—';
  if (rule === 'event') return ctx.eventLabel(values.event_key as string) ?? String(values.event_key ?? 'an event');
  return DONE_RULES[rule] ?? rule;
}

/** The columns a change proposes that differ from what the row held (an event key carried along unchanged is not a change). */
export function changedColumns(c: Pick<StepChangeRow, 'proposed' | 'before' | 'applied'>): string[] {
  const v = c.applied ?? c.proposed;
  return Object.keys(v).filter((k) => k !== 'position' && !(k in c.before && JSON.stringify(c.before[k] ?? null) === JSON.stringify(v[k] ?? null)));
}

/** What Approve writes, or what was written: the same sentence the Standup card carries. */
export function stepChangeWords(c: StepChangeRow, ctx: StepChangeContext): string {
  const v = c.applied ?? c.proposed;
  const stage = ctx.stageName(c.stage_id);
  const name = c.target_table === 'pipeline_stage' ? stage : ctx.stepName(c.target_table, c.target_id);
  if (c.kind === 'add') return `add "${String(v.name ?? '?')}" as step ${String(v.position ?? '?')}${stage ? ` of ${stage}` : ' of the job steps'}`;
  if (c.kind === 'retire') {
    const repl = c.target_table === 'pipeline_step' ? ctx.stepName('pipeline_step', v.replaced_by_step_id as string) : null;
    const n = c.target_table === 'pipeline_step' && c.target_id ? ctx.leadsOn(c.target_id) : 0;
    return `retire "${name ?? '?'}"${repl ? (n > 0 ? ` · moves ${n} lead${n === 1 ? '' : 's'} to "${repl}"` : ` · "${repl}" takes over`) : ''}`;
  }
  if (c.kind === 'instructions') return `new Instructions on "${name ?? '?'}"`;
  const parts = changedColumns(c).map((k) => `${stepColumnLabel(c.target_table, k)} → ${stepValueWords(c.target_table, k, v[k], ctx)}`);
  return `${c.kind === 'stage_setting' ? stage ?? '?' : `"${name ?? '?'}"`}: ${parts.join('; ') || 'nothing'}`;
}

/** The button's words: "Approve · add … as step 2 of 6", "Approve · retire step 6 · moves 2 leads to step 7". */
export function approveWords(c: StepChangeRow, ctx: StepChangeContext): string {
  return `Approve · ${stepChangeWords(c, ctx)}`;
}

/**
 * Whether a waiting suggestion is stale: the row no longer holds what it held when filed (`now` is the row's columns as they
 * stand, quiet_days laid on for a stage). An add is stale when a live step of that name has since appeared where it would go.
 */
export function stepChangeStale(c: StepChangeRow, now: Record<string, unknown> | null, sameNameLive = false): boolean {
  if (c.status !== 'waiting') return false;
  if (c.kind === 'add') return sameNameLive;
  if (!now) return true;
  if (c.kind === 'retire') return now.retired_at !== null && now.retired_at !== undefined;
  return Object.keys(c.before).some((k) => JSON.stringify(now[k] ?? null) !== JSON.stringify(c.before[k] ?? null));
}

/** Whether an applied or approved change still stands on its row (its written values are the row's), so Undo is offered. */
export function stepChangeStands(c: StepChangeRow, now: Record<string, unknown> | null): boolean {
  if (c.status !== 'applied' && c.status !== 'approved') return false;
  if (!now) return false;
  if (c.kind === 'add') return now.retired_at === null || now.retired_at === undefined;
  if (c.kind === 'retire') return now.retired_at !== null && now.retired_at !== undefined;
  const v = c.applied ?? c.proposed;
  return Object.keys(v).filter((k) => k !== 'position').every((k) => JSON.stringify(now[k] ?? null) === JSON.stringify(v[k] ?? null));
}

/** The newest change that still stands on each row: the one whose who, when and Undo the row shows. */
export function standingChanges(changes: StepChangeRow[], nowOf: (c: StepChangeRow) => Record<string, unknown> | null): Map<string, StepChangeRow> {
  const out = new Map<string, StepChangeRow>();
  for (const c of [...changes].sort((a, b) => String(b.decided_at ?? b.suggested_at).localeCompare(String(a.decided_at ?? a.suggested_at)))) {
    if (!c.target_id || out.has(c.target_id)) continue;
    if (stepChangeStands(c, nowOf(c))) out.set(c.target_id, c);
  }
  return out;
}

/** A line of reason cut at a word (the right column shows one line; the full text on tap). */
export function oneLine(text: string | null | undefined, max = 72): string {
  const t = (text ?? '').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const at = cut.lastIndexOf(' ');
  return `${(at > max * 0.5 ? cut.slice(0, at) : cut).trimEnd()}…`;
}
